import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';

const OregonRequirementsPage = () => {
  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />
      
      <main className="flex-grow">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-green-50 via-white to-gray-50 py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 bg-green-100 text-green-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
                <i className="ri-government-line"></i>
                State Compliance
              </div>
              <h1 className="text-5xl font-bold text-gray-900 mb-6">
                Oregon <span className="text-green-600">Cybersecurity Requirements</span>
              </h1>
              <p className="text-xl text-gray-600 mb-8">
                Understanding Oregon's Consumer Information Protection Act (OCIPA) and its requirements for protecting personal information of Oregon residents.
              </p>
            </div>
          </div>
        </section>

        {/* Disclaimer */}
        <section className="py-8 bg-yellow-50 border-y border-yellow-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-start gap-4">
                <i className="ri-information-line text-3xl text-yellow-600 flex-shrink-0"></i>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Important Disclaimer</h3>
                  <p className="text-gray-700 text-sm">
                    The information provided on this page is for general informational purposes only. State cybersecurity regulations are complex and subject to change. We recommend reaching out to our team to confirm you have covered all applicable requirements for Oregon operations.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <div className="space-y-8">
                {/* Overview */}
                <div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-4">Overview</h2>
                  <p className="text-gray-700 text-lg">
                    Oregon regulates data security and breach notification largely through the <strong>Oregon Consumer Information Protection Act (OCIPA)</strong>, codified at ORS 646A.600–646A.628. The law requires organizations to safeguard certain personal information of Oregon residents and to notify consumers (and in some cases the Oregon Department of Justice) when breaches occur.
                  </p>
                </div>

                {/* Who is Covered */}
                <div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-4">Who is Covered?</h2>
                  <div className="bg-green-50 rounded-lg p-6 border-l-4 border-green-600">
                    <ul className="space-y-3 text-gray-700">
                      <li className="flex items-start gap-3">
                        <i className="ri-checkbox-circle-fill text-green-600 flex-shrink-0 mt-1 text-xl"></i>
                        <span>Any person or entity that owns, licenses, or maintains personal information about Oregon residents in connection with business or governmental operations</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <i className="ri-checkbox-circle-fill text-green-600 flex-shrink-0 mt-1 text-xl"></i>
                        <span>Includes financial institutions, mortgage lenders, servicers, and other companies storing consumer data</span>
                      </li>
                    </ul>
                  </div>
                </div>

                {/* Security Expectations */}
                <div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-6">Security and Program Expectations</h2>
                  <p className="text-gray-700 text-lg mb-6">
                    While OCIPA focuses heavily on breach response and notification, it also expects reasonable security practices:
                  </p>
                  
                  <div className="space-y-6">
                    <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm">
                      <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                        <i className="ri-shield-check-line text-green-600"></i>
                        Reasonable Safeguards
                      </h3>
                      <ul className="space-y-2 text-gray-700">
                        <li className="flex items-start gap-2">
                          <i className="ri-arrow-right-s-line text-green-600 flex-shrink-0 mt-1"></i>
                          <span>Businesses must implement and maintain reasonable security procedures and practices to protect personal information from unauthorized access, acquisition, destruction, use, modification, or disclosure</span>
                        </li>
                      </ul>
                    </div>

                    <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm">
                      <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                        <i className="ri-team-line text-green-600"></i>
                        Vendor Management
                      </h3>
                      <ul className="space-y-2 text-gray-700">
                        <li className="flex items-start gap-2">
                          <i className="ri-arrow-right-s-line text-green-600 flex-shrink-0 mt-1"></i>
                          <span>If an organization discloses personal information to a third party, it must ensure that the third party also maintains reasonable security safeguards</span>
                        </li>
                      </ul>
                    </div>

                    <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm">
                      <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                        <i className="ri-alarm-warning-line text-green-600"></i>
                        Incident Response Planning
                      </h3>
                      <ul className="space-y-2 text-gray-700">
                        <li className="flex items-start gap-2">
                          <i className="ri-arrow-right-s-line text-green-600 flex-shrink-0 mt-1"></i>
                          <span>State guidance emphasizes that agencies and organizations should have written incident response plans and coordinate with applicable regulations when handling security incidents</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Breach Notification */}
                <div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-4">Breach Notification Obligations</h2>
                  <div className="bg-red-50 rounded-lg p-6 border-l-4 border-red-600">
                    <ul className="space-y-3 text-gray-700">
                      <li className="flex items-start gap-3">
                        <i className="ri-alert-line text-red-600 flex-shrink-0 mt-1 text-xl"></i>
                        <span>If a "breach of security" involving Oregon consumers' personal information occurs, businesses must notify affected consumers within 45 days of discovering the breach, with limited exceptions (e.g., law enforcement delays)</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <i className="ri-alert-line text-red-600 flex-shrink-0 mt-1 text-xl"></i>
                        <span>If more than 250 Oregon residents are affected, the organization must also notify the Oregon Department of Justice, providing a copy of the consumer notice and additional details</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <i className="ri-alert-line text-red-600 flex-shrink-0 mt-1 text-xl"></i>
                        <span>Specific content and methods of notification are prescribed, and credit monitoring must sometimes be offered, depending on the nature of the breach</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-gradient-to-br from-gray-50 to-green-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Need Help with Oregon Compliance?
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                Our team helps businesses understand and implement the controls needed to meet Oregon's Consumer Information Protection Act requirements.
              </p>
              <div className="flex flex-wrap gap-4 justify-center">
                <a href="/schedule-call" className="inline-block">
                  <button className="inline-flex items-center px-6 py-3 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 transition-colors shadow-md whitespace-nowrap cursor-pointer">
                    <i className="ri-calendar-line mr-2"></i>
                    Schedule Consultation
                  </button>
                </a>
                <a href="/risk-assessment" className="inline-block">
                  <button className="inline-flex items-center px-6 py-3 bg-white text-green-600 font-semibold rounded-lg hover:bg-gray-50 transition-colors border-2 border-green-600 whitespace-nowrap cursor-pointer">
                    <i className="ri-shield-check-line mr-2"></i>
                    Get Compliance Assessment
                  </button>
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default OregonRequirementsPage;
